import React, { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { AlertMessage } from './ui/AlertMessage';
import { Modal } from './ui/Modal';
import { AuthenticatedImage } from './ui/AuthenticatedImage';
import { getAuthHeaders, API_BASE_URL } from '../api/mediaUrl';
import { clearAuthStorage } from '../utils/authSession';

const getApiErrorMessage = (errData, fallback) => {
  if (!errData) {
    return fallback;
  }
  if (typeof errData.error === 'string' && errData.error.trim()) {
    return errData.error;
  }
  if (typeof errData.message === 'string' && errData.message.trim()) {
    return errData.message;
  }
  if (typeof errData === 'object') {
    const first = Object.values(errData).find((v) => typeof v === 'string' && v.trim());
    if (first) {
      return first;
    }
  }
  return fallback;
};

function MyProfileModal({ onClose, onProfileUpdated }) {
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    mobile: '',
    profileImage: '',
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [initialEmail, setInitialEmail] = useState('');

  const token = localStorage.getItem('token');
  const headers = useMemo(() => {
    const h = { 'Content-Type': 'application/json' };
    if (token) {
      h.Authorization = `Bearer ${token}`;
    }
    return h;
  }, [token]);

  useEffect(() => {
    const fetchMyProfile = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/me`, { headers });
        if (!response.ok) {
          throw new Error('Failed to load profile');
        }
        const data = await response.json();
        setForm({
          name: data.username || '',
          email: data.email || '',
          mobile: data.mobile || '',
          profileImage: data.profileImage || '',
        });
        setInitialEmail(data.email || '');
      } catch (err) {
        setError(err.message || 'Unable to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchMyProfile();
  }, [headers]);

  useEffect(() => {
    if (!error) {
      return undefined;
    }
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) {
      return undefined;
    }
    const timer = setTimeout(() => setSuccess(''), 3500);
    return () => clearTimeout(timer);
  }, [success]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    setUploadingImage(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE_URL}/api/files/upload/profile`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(getApiErrorMessage(errData, 'Unable to upload profile image'));
      }
      const uploaded = await response.json();
      setForm((prev) => ({ ...prev, profileImage: uploaded.key || '' }));
    } catch (err) {
      setError(err.message || 'Unable to upload profile image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSavingProfile(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/me/profile`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          mobile: form.mobile.trim(),
          profileImage: form.profileImage || null,
        }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(getApiErrorMessage(errData, 'Failed to update profile'));
      }
      const updated = await response.json();
      onProfileUpdated(updated);
      if ((updated.email || '').toLowerCase() !== (initialEmail || '').toLowerCase()) {
        setSuccess('Email updated successfully. Please sign in again.');
        setTimeout(() => {
          clearAuthStorage();
          window.location.href = '/';
        }, 1200);
        return;
      }
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.message || 'Unable to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }

    setSavingPassword(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/me/password`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(passwordForm),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(getApiErrorMessage(errData, 'Failed to update password'));
      }
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setSuccess('Password updated successfully');
    } catch (err) {
      setError(err.message || 'Unable to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="My profile"
      description="Update your personal information and password"
      size="xl"
      icon={(
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )}
      bodyClassName="bg-slate-50/50"
    >
      <div className="py-5 sm:py-6 space-y-5">
        <AlertMessage message={error} type="error" onClose={() => setError('')} />
        <AlertMessage message={success} type="success" onClose={() => setSuccess('')} />

        {loading ? (
          <div className="py-6 text-center text-slate-500">Loading profile...</div>
        ) : (
          <>
            <form onSubmit={handleProfileSave} className="rounded-2xl border border-slate-200/80 bg-white shadow-sm p-5 space-y-5">
              <div className="flex items-center gap-4">
                <label className="cursor-pointer group shrink-0">
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onImageChange} />
                  {form.profileImage ? (
                    <AuthenticatedImage src={form.profileImage} alt="Profile" className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-md ring-2 ring-primary-100 group-hover:ring-primary-200 transition-shadow" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 border-2 border-dashed border-slate-200 group-hover:border-primary-300 group-hover:text-primary-700 transition-colors text-sm font-medium">
                      Upload
                    </div>
                  )}
                </label>
                <div className="min-w-0">
                  <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Personal information</h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Click the photo to upload or change your profile image.
                    {uploadingImage ? ' Uploading…' : ''}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Name *" value={form.name} onChange={(e) => updateField('name', e.target.value)} required />
                <Input label="Email *" type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} required />
                <Input label="Mobile" value={form.mobile} onChange={(e) => updateField('mobile', e.target.value.replace(/\D/g, ''))} />
              </div>
              <div className="flex justify-end pt-2 border-t border-slate-100">
                <Button type="submit" variant="primary" loading={savingProfile} disabled={savingProfile}>
                  Save profile
                </Button>
              </div>
            </form>

            <form onSubmit={handlePasswordSave} className="rounded-2xl border border-slate-200/80 bg-white shadow-sm p-5 space-y-5">
              <div>
                <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Change password</h4>
                <p className="text-xs text-slate-500 mt-0.5">Must contain uppercase, lowercase, number and a special character.</p>
              </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="Current Password *"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                    required
                  />
                  <Input
                    label="New Password *"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                    minLength={8}
                    maxLength={100}
                    pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@#$%^&+=!]).{8,100}$"
                    title="Password must contain uppercase, lowercase, number and special character"
                    required
                  />
                  <Input
                    label="Confirm Password *"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                    required
                  />
                </div>
              <div className="flex justify-end pt-2 border-t border-slate-100">
                <Button type="submit" variant="primary" loading={savingPassword} disabled={savingPassword}>
                  Update password
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </Modal>
  );
}

export default MyProfileModal;
